/**
 * Embed (iframe) session state — SER-5886.
 *
 * LEAF MODULE by design (no app imports): it is consumed by the axios service
 * factory and the SSE stream helper, and gets exercised directly in Bun tests
 * — importing app modules here would recreate the mock.module leak surface
 * (repo failure mode #1).
 *
 * Storage contract: the opaque `ebt_` session lives in sessionStorage ONLY.
 * sessionStorage is per-tab and dies with the tab, so an embed session can
 * never leak into (or wipe) the real console's localStorage['auth'] session —
 * a logged-in console tab stays logged in next to an embed iframe.
 */

export interface REmbedSession {
  session_token: string
  session_id: string
  auth_id: string
  // The sub-account this session is scoped to, or '' for a main account.
  // Returned by both exchange and the session/me probe. OPTIONAL because a
  // session persisted before this field existed will not carry it.
  //
  // This is a UI-SHAPING HINT, never a control: the actual gate is hodor's
  // route allow-list plus contacto-core's ownership checks, both server-side.
  // It exists so sub-account-denied tabs can be hidden instead of rendering
  // and then 403-ing, which reads as a broken product.
  sub_auth_id?: string
  // The actor this session writes as, and the ONLY value that names its realtime
  // channel. The runner announces a playground run on `aom.<uuid>` and execution
  // grants exactly that channel, so without it the iframe cannot subscribe to
  // its own run and the Test agent transcript never arrives.
  //
  // NOT interchangeable with org_uuid: that one is the PubNub *userId*, which
  // has to match the grant's authorized UUID. They are different values and both
  // are needed.
  aom_uuid?: string
  region: string
  permissions: string[]
  // Origins the mint request allowed. Carried on the session so a page reload
  // can re-pin its postMessage parent without re-minting.
  allowed_origins: string[]
  expires_at: string
  // The session org's creation timestamp, captured from the session/me probe
  // during bootstrap. Embed has no profile query, so this is the only source
  // for the LiveKit post-cutoff auto-enable (see use-livekit-enabled.ts).
  // Optional: absent if the probe didn't return it (falls back to the flag).
  org_created_at?: string
  // Org identity + PubNub keyset, harvested from the same session/me probe
  // (SER-5886 Phase 1). They power the embedded Test agent's live transcript:
  // use-pubnub derives the Pubnub instance from these instead of the (embed-
  // disabled) profile query. publish/subscribe keys are client-public by
  // design — PAM tokens are the access control. All optional: absent fields
  // simply leave PubNub unregistered (Test agent degrades gracefully).
  org_uuid?: string
  pubnub_publish_key?: string
  pubnub_subscribe_key?: string
  // Whether hodor BOUND this session to the browser's non-extractable signing
  // key at exchange (SER-5887). Reported by hodor, never inferred here.
  //
  // The bootstrap needs it to tell two states apart that otherwise look
  // identical: "bound, but the key is gone" — unrecoverable, revoke and
  // re-mint — versus "never bound, and this browser has no IndexedDB at all",
  // which is fine and must keep working. Without this flag the second case
  // would be revoked on every reload.
  dpop_bound?: boolean
}

const STORAGE_KEY_BASE = 'embed_auth'

/** Query parameter carrying the loader's per-instance id. Set by the loader on
 * the iframe src; read here only to namespace storage. */
export const EMBED_INSTANCE_PARAM = '_i'

/**
 * The sessionStorage key for THIS iframe instance.
 *
 * sessionStorage is partitioned by origin and top-level tab — NOT per iframe —
 * so every same-origin embed iframe in one tab shares a storage area. With a
 * single global key that meant:
 *
 *   - two iframes open at once clobbered each other's session, and
 *   - destroying an iframe and mounting a new one for a DIFFERENT customer
 *     re-used the previous customer's session, because the bootstrap's
 *     reload fast-path accepts any unexpired entry and cannot know which
 *     account this iframe was opened for.
 *
 * That is tenant confusion, not a cosmetic bug. Namespacing by the loader's
 * per-instance id fixes all of it: a new mount is a new instance, so it cannot
 * see the previous one's session, while a genuine reload keeps the same src —
 * and therefore the same id — and still re-uses it.
 *
 * Falls back to the bare key when no instance id is present, which keeps a
 * pre-upgrade loader working exactly as before rather than silently losing its
 * session.
 */
// Captured ONCE, at module load, from the URL the iframe booted with.
//
// It MUST NOT be re-read per call. The loader puts `_i` only on the initial
// iframe src, and in-iframe SPA navigation replaces the search string wholesale
// — `useAppNavigate`/`toEmbedRoutePath` rewrite the pathname and carry no query.
// So a per-call read returns the id on /embed/agents and nothing on
// /embed/agent/builder/<id>, which silently moves the session to a different
// key: opening any agent found no session, asked the parent for a token the
// loader had already marked delivered, and dead-ended in a 10-second spinner
// followed by `no_token`. Reading once pins the slot for the life of the
// document, which is exactly the lifetime the session has.
const INSTANCE_ID = ((): string => {
  try {
    return new URLSearchParams(window.location.search).get(EMBED_INSTANCE_PARAM) ?? ''
  } catch {
    return ''
  }
})()

/**
 * The storage key for a given instance id. Pure, and exported so the isolation
 * rules are testable without reloading the module (INSTANCE_ID is captured once
 * by design — see above).
 *
 * An empty id falls back to the bare key, which keeps a pre-upgrade loader
 * working exactly as before rather than silently losing its session.
 */
export const embedStorageKey = (instanceId: string): string =>
  instanceId ? `${STORAGE_KEY_BASE}:${instanceId}` : STORAGE_KEY_BASE

const storageKey = (): string => embedStorageKey(INSTANCE_ID)

/**
 * This iframe instance's id, captured once at module load.
 *
 * Exported so the DPoP key store can namespace by the SAME instance the session
 * uses. Re-deriving it there would duplicate the read-once-at-module-load rule
 * above, and getting that wrong is precisely the bug it documents: an in-iframe
 * navigation drops the `_i` query param, so a per-call read silently moves to a
 * different key mid-session.
 */
export const embedInstanceId = (): string => INSTANCE_ID

/** Custom window event dispatched when the embed session gets a 401 —
 * the embed layout listens and shows the session-expired overlay +
 * notifies the parent page via postMessage. NEVER window.cx.logout. */
export const EMBED_SESSION_EXPIRED_EVENT = 'embed:session-expired'

/** Dispatched by setEmbedSession AFTER the session is persisted. Root-level
 * providers (mounted above the embed bootstrap, e.g. FeatureFlagsProvider)
 * render before the exchange completes and would otherwise never re-evaluate
 * their session-gated queries — this event is their re-render signal. */
export const EMBED_SESSION_READY_EVENT = 'embed:session-ready'

/** Embed mode is a ROUTE property: every page under /embed runs with the
 * embed service branch; everything else is untouched console behavior. */
export const isEmbedMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  const path = window.location.pathname
  // Segment-boundary match so a future top-level route like `/embeddings`
  // can't be misread as embed mode.
  return path === '/embed' || path.startsWith('/embed/')
}

export const getEmbedSession = (): REmbedSession | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey())
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as REmbedSession
    // Shape-validate ONCE at the storage boundary so every consumer can trust
    // the REmbedSession type as written — no per-call-site re-guarding. A
    // stale/hand-edited entry that fails the check reads as "no session".
    //
    // session_id is checked here, and it is a SECURITY check rather than a
    // hygiene one. It is this session's playground-run ref, and
    // shouldAdoptRunAnnouncement refuses a foreign run only when BOTH sides are
    // known — so a partner end-user who deleted just this one field from
    // sessionStorage kept a fully working session while making their own ref
    // undefined, and then adopted whichever announcement landed first on the
    // shared machine AOM: another user's transcript, node configs and system
    // prompt. Validating it here is what makes that impossible, rather than
    // re-checking at the adoption site: you cannot hold a working session
    // WITHOUT the field, because dropping it invalidates the whole record and
    // the iframe re-bootstraps.
    //
    // No rollout risk: exchange has always returned session_id and the type
    // declares it non-optional.
    if (
      typeof parsed?.session_token !== 'string' ||
      parsed.session_token === '' ||
      typeof parsed.session_id !== 'string' ||
      parsed.session_id === '' ||
      !Array.isArray(parsed.allowed_origins)
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * The embed session's org auth id (the org's auth id — hodor resolves the org
 * row from it), or '' when there is no session. Canonical accessor for the
 * "in embed, the auth id comes from the session, not a console profile/token
 * query" rule shared by the KB engine resolution and the playground chat
 * publish. Mirrors the mode-scoped `getActiveRegion()` in create-service.ts.
 */
export const getEmbedAuthId = (): string => getEmbedSession()?.auth_id ?? ''

/**
 * The sub-account this embed session is scoped to, or '' for a main-account
 * session (and for any non-embed context). Mirrors `getEmbedAuthId` above.
 *
 * Use it only to SHAPE THE UI — hide a tab, drop a node type from the palette.
 * It is derived from a value the browser holds, so it can never be the control;
 * every route a sub-account must not reach is already denied by hodor's
 * allow-list and re-checked against flow ownership in contacto-core.
 */
export const getEmbedSubAuthId = (): string => getEmbedSession()?.sub_auth_id ?? ''

/**
 * True when this iframe is running as a SUB-ACCOUNT (a customer of the
 * partner), false for a main-account session or outside embed entirely.
 * The predicate the tab/palette gates read.
 *
 * The `isEmbedMode()` term is what makes "the console is unaffected" a STRUCTURAL
 * guarantee rather than a circumstantial one. Session storage is per-TAB, not
 * per-frame, so a tab that has been on an `/embed/*` route still carries the
 * session object afterwards. Without this term a console route reached in that
 * same tab would read as a sub-account and start hiding console tabs and palette
 * nodes. Hard to reach today, but the cost of ruling it out is one call.
 */
export const isSubAccountEmbedSession = (): boolean => isEmbedMode() && getEmbedSubAuthId() !== ''

/**
 * Persist the session and report whether it is actually retrievable.
 *
 * Returns false in a storage-blocked context (third-party storage partitioned
 * or cookies disabled in the iframe). The caller MUST surface that: every
 * later request reads the session back out of storage, so silently continuing
 * tells the host "ready" and then fails every API call with no explanation.
 */
export const setEmbedSession = (session: REmbedSession): boolean => {
  let persisted = false
  try {
    const key = storageKey()
    const payload = JSON.stringify(session)
    // Clear first, so a discarded write cannot be masked by whatever was already
    // at this key. Without this, a reload into the same iframe instance reuses the
    // key, and a store that accepts-then-discards leaves the PREVIOUS session
    // readable — see the read-back below for why that matters.
    window.sessionStorage.removeItem(key)
    window.sessionStorage.setItem(key, payload)
    // Read back rather than trusting setItem to have worked: a partitioned or
    // blocked store can accept the write and discard it without throwing.
    //
    // Compare against the payload, NOT `!== null`. A presence check only proves
    // that SOMETHING is stored — so a stale entry plus a silently-discarded write
    // reported success, bootstrap announced the session ready, and every
    // interceptor then read the old token and failed with no explanation. The
    // whole point of this return value is to surface a blocked store, and a
    // presence check is exactly the case it cannot see.
    persisted = window.sessionStorage.getItem(key) === payload
  } catch {
    persisted = false
  }
  // Fire even when storage failed: consumers read via getEmbedSession, so a
  // no-op notification is harmless, while a missed one strands root providers.
  window.dispatchEvent(new Event(EMBED_SESSION_READY_EVENT))
  return persisted
}

export const clearEmbedSession = (): void => {
  try {
    window.sessionStorage.removeItem(storageKey())
  } catch {
    // Same storage-blocked context — clearing is best-effort.
  }
}

/**
 * Rewrites the first `/v{n}/` segment to `/v{n}/embed/` so every service call
 * lands on hodor's embed gateway (which enforces the route allow-list and
 * injects the tenant server-side). Version-generic because some services pin
 * higher path versions (legacy flow trigger `/v2/account/...`, external-apps
 * `/v2|/v3/...`) and hodor registers the matching `/v{n}/embed/*` twins.
 * Self-gated on embed mode (identity elsewhere) and idempotent, matching
 * `toEmbedRoutePath` — callers never need their own isEmbedMode() dance.
 * Works on both baseURLs (`https://host/v1/contacto-core…`) and absolute
 * paths (`/v2/ai-assist/…`).
 */
export const toEmbedPath = (url: string): string => {
  if (!isEmbedMode() || !url || /\/v\d+\/embed\//.test(url)) {
    return url
  }
  return url.replace(/\/(v\d+)\//, '/$1/embed/')
}

export const dispatchEmbedSessionExpired = (): void => {
  window.dispatchEvent(new Event(EMBED_SESSION_EXPIRED_EVENT))
}

/**
 * Router-path counterpart of `toEmbedPath` (which rewrites API URLs). In embed
 * mode every in-iframe navigation must stay under `/embed`, otherwise it lands
 * on a `ProtectedRoute` and renders the console login form INSIDE the customer's
 * iframe. Prefixes a console router path (e.g. `/agent/builder/123?new`) with
 * `/embed` so `<embed>/agent/builder/123` maps to the embed route. Outside embed
 * mode, or for an already-prefixed path, it returns the path unchanged.
 *
 * "Already prefixed" is a SEGMENT test, matching `isEmbedMode()` above and
 * `validation.config.ts`. A `startsWith('/embed')` here was the third spelling
 * of one rule in this PR, and the loose one: a route like `/embeddings` read as
 * already-prefixed, so it was returned untouched, escaped `/embed/*`, hit a
 * ProtectedRoute and rendered the console LOGIN FORM inside the customer's
 * iframe — the exact failure this helper exists to prevent.
 */
export const toEmbedRoutePath = (path: string): string =>
  isEmbedMode() && !/^\/embed(\/|$)/.test(path) ? `/embed${path}` : path
